/**
 * Non-blocking email verification banner.
 * Shows a dismissible info banner when user's email is not verified.
 * Does NOT block any functionality -- just a gentle reminder.
 *
 * Features:
 * - Shows "Verify your email" message with resend button
 * - Dismissible (remembers in localStorage for 24 hours)
 * - Compact design matching Five AI brand
 * - Loading state while sending verification email
 * - Success state after resend
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, X, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { BrandedSpinner } from '@/components/common/BrandedLoading';
import { resendVerificationEmail } from '@/services/emailVerificationApi';

const DISMISS_KEY = 'five_ai_email_verify_dismissed';
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

function isDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (isNaN(ts)) return false;
    return Date.now() - ts < DISMISS_DURATION_MS;
  } catch {
    return false;
  }
}

function dismiss(): void {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    // ignore
  }
}

export interface EmailVerificationBannerProps {
  email?: string;
  emailVerified?: boolean;
  className?: string;
}

export const EmailVerificationBanner: React.FC<EmailVerificationBannerProps> = ({
  email,
  emailVerified,
  className,
}) => {
  const [visible, setVisible] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Only show if emailVerified is explicitly false (not undefined/null/true)
    if (emailVerified === false && !isDismissed()) {
      setVisible(true);
    } else {
      setVisible(false);
    }
  }, [emailVerified]);

  const handleDismiss = useCallback(() => {
    dismiss();
    setVisible(false);
  }, []);

  const handleResend = useCallback(async () => {
    setSending(true);
    setError(null);
    setSent(false);
    try {
      await resendVerificationEmail();
      setSent(true);
      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        setSent(false);
      }, 5000);
    } catch {
      setError('Failed to send. Try again later.');
    } finally {
      setSending(false);
    }
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className={className}
        >
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 dark:bg-amber-500/10 px-4 py-3 shadow-sm">
            <div className="flex items-start gap-3">
              {/* Icon */}
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-500/15 text-amber-600 dark:text-amber-400 mt-0.5">
                <Mail className="h-4 w-4" />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                {sent ? (
                  <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400 font-medium">
                    <CheckCircle2 className="h-4 w-4 shrink-0" />
                    Verification email sent! Check your inbox.
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-amber-800 dark:text-amber-200 font-medium">
                      Please verify your email address
                      {email ? (
                        <span className="font-normal text-amber-700 dark:text-amber-300">
                          {' '}({email})
                        </span>
                      ) : null}
                    </p>
                    {error && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">{error}</p>
                    )}
                  </>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 shrink-0">
                {!sent && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 rounded-lg text-xs border-amber-500/30 text-amber-700 dark:text-amber-300 hover:bg-amber-500/10"
                    onClick={handleResend}
                    disabled={sending}
                  >
                    {sending ? (
                      <>
                        <BrandedSpinner sizeClassName="h-3 w-3" className="mr-1" />
                        Sending...
                      </>
                    ) : (
                      'Resend verification email'
                    )}
                  </Button>
                )}
                <button
                  type="button"
                  onClick={handleDismiss}
                  className="flex h-6 w-6 items-center justify-center rounded-md text-amber-600/60 hover:text-amber-600 dark:text-amber-400/60 dark:hover:text-amber-400 hover:bg-amber-500/10 transition-colors"
                  aria-label="Dismiss"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
